import type { ProjectSlug } from './projects';

/**
 * Central registry of project capabilities ("features"). Single source of
 * truth for:
 *   • the chips rendered on InfoPanel + ProjectFeed
 *   • the keywords emitted in /grid JSON-LD and /llms.txt
 *   • the validation list used by `npm run project:add`
 *   • the project ↔ feature mapping (each entry owns the list of slugs
 *     that have it — open this file to see at a glance who uses what)
 *
 * Adding a feature:
 *   1. Add an entry below (uppercase key + PT/EN label + PT/EN
 *      description + the slugs that have it).
 *   2. ProjectSlug autocompletes the slug list. Typos error.
 *   3. The detection heuristic in `scripts/add-project.ts` is optional
 *      but recommended; add a regex there if the feature is detectable
 *      from a site's rendered text/DOM.
 *
 * Adding a project to an existing feature:
 *   Append the slug to that feature's `projects:` array. That's the
 *   only place to touch.
 */
export type FeatureMeta = {
  label: { pt: string; en: string };
  description: { pt: string; en: string };
  projects: readonly ProjectSlug[];
};

export const FEATURES = {
  AI: {
    label: { pt: 'IA', en: 'AI' },
    description: {
      pt: 'Workflows com inteligência artificial que automatizam tarefas e validam dados.',
      en: 'AI workflows that automate tasks and validate data.',
    },
    projects: ['ficha'],
  },
  ANALYTICS: {
    label: { pt: 'Analytics', en: 'Analytics' },
    description: {
      pt: 'Dashboard de métricas e business intelligence sobre os dados da operação.',
      en: 'Metrics dashboard and business intelligence over operational data.',
    },
    projects: [],
  },
  API: {
    label: { pt: 'API', en: 'API' },
    description: {
      pt: 'Endpoints públicos ou privados para integração com sistemas externos.',
      en: 'Public or private endpoints for third-party integration.',
    },
    projects: ['hu2i'],
  },
  AUTH: {
    label: { pt: 'Autenticação', en: 'Authentication' },
    description: {
      pt: 'Login e área de cliente protegida.',
      en: 'Login and protected client area.',
    },
    projects: ['connect-energy', 'ficha'],
  },
  BLOG: {
    label: { pt: 'Blog', en: 'Blog' },
    description: {
      pt: 'Sistema de publicação de artigos.',
      en: 'Article publishing system.',
    },
    projects: ['click-saude', 'primis'],
  },
  BOOKING: {
    label: { pt: 'Marcação online', en: 'Online booking' },
    description: {
      pt: 'Sistema de marcação de consultas, visitas ou reservas.',
      en: 'Appointment, visit or reservation booking system.',
    },
    projects: ['click-saude', 'clinihorta', 'health2u', 'pico-island-villa'],
  },
  CHATBOT: {
    label: { pt: 'Chatbot', en: 'Chatbot' },
    description: {
      pt: 'Assistente conversacional com IA para atendimento ou triagem.',
      en: 'Conversational AI assistant for support or triage.',
    },
    projects: ['health2u', 'pico-island-villa', 'ficha', 'connect-energy' ],
  },
  CMS: {
    label: { pt: 'CMS', en: 'CMS' },
    description: {
      pt: 'Conteúdo editável pelo cliente sem programação.',
      en: 'No-code editable content for the client.',
    },
    projects: [
      'click-saude',
      'hu2i',
      'primis',
    ],
  },
  CRM: {
    label: { pt: 'CRM', en: 'CRM' },
    description: {
      pt: 'Gestão de leads, contactos e pipeline comercial.',
      en: 'Lead, contact and sales pipeline management.',
    },
    projects: ['connect-energy'],
  },
  DASHBOARD: {
    label: { pt: 'Dashboard', en: 'Dashboard' },
    description: {
      pt: 'Painel interno de gestão da plataforma com permissões por perfil.',
      en: 'Internal management panel with role-based permissions.',
    },
    projects: ['connect-energy','ficha'],
  },
  ECOMMERCE: {
    label: { pt: 'E-commerce', en: 'E-commerce' },
    description: {
      pt: 'Catálogo, carrinho e checkout completos com gestão de stock.',
      en: 'Full catalogue, cart and checkout with inventory management.',
    },
    projects: ['ficha'],
  },
  EU_FUNDING: {
    label: { pt: 'Fundos europeus', en: 'EU funding' },
    description: {
      pt: 'Plataforma orientada a fundos europeus (PRR, Horizon, FEDER).',
      en: 'Platform tailored to European funding programmes (PRR, Horizon, FEDER).',
    },
    projects: ['ficha'],
  },
  GALLERY: {
    label: { pt: 'Galeria', en: 'Gallery' },
    description: {
      pt: 'Galeria de imagens com lightbox.',
      en: 'Image gallery with lightbox.',
    },
    projects: ['pico-island-villa'],
  },
  I18N: {
    label: { pt: 'Multilíngue', en: 'Multilingual' },
    description: {
      pt: 'Versões em múltiplas línguas.',
      en: 'Multiple language versions.',
    },
    projects: ['pico-island-villa'],
  },
  MULTI_TENANT: {
    label: { pt: 'Multi-cliente', en: 'Multi-tenant' },
    description: {
      pt: 'SaaS com workspace dedicado por cliente, isolado em dados e permissões.',
      en: 'SaaS with a dedicated workspace per customer, isolated in data and permissions.',
    },
    projects: ['clinihorta'],
  },
  PAYMENTS: {
    label: { pt: 'Pagamentos', en: 'Payments' },
    description: {
      pt: 'Checkout integrado com gateway (cartão, MB Way, Stripe).',
      en: 'Integrated checkout with payment gateway (card, MB Way, Stripe).',
    },
    projects: ['ficha'],
  },
  REAL_TIME: {
    label: { pt: 'Tempo real', en: 'Real-time' },
    description: {
      pt: 'Atualizações em tempo real via websockets ou server-sent events.',
      en: 'Real-time updates via websockets or server-sent events.',
    },
    projects: [],
  },
  SEO: {
    label: { pt: 'SEO', en: 'SEO' },
    description: {
      pt: 'Otimização para motores de busca (estrutura, OG, sitemap).',
      en: 'Search engine optimization (structure, OG, sitemap).',
    },
    projects: ['click-saude', 'hu2i', 'connect-energy', 'primis', 'ficha'],
  },
  VIDEO: {
    label: { pt: 'Vídeo', en: 'Video' },
    description: {
      pt: 'Videoconsultas ou chamadas integradas.',
      en: 'Integrated video consultations or calls.',
    },
    projects: [],
  },
  WHATSAPP: {
    label: { pt: 'WhatsApp', en: 'WhatsApp' },
    description: {
      pt: 'WhatsApp Business integrado para atendimento ou marcação.',
      en: 'Integrated WhatsApp Business for support or booking.',
    },
    projects: ['clinihorta'],
  },
} as const satisfies Record<string, FeatureMeta>;

export type FeatureKey = keyof typeof FEATURES;

export const FEATURE_KEYS = Object.keys(FEATURES) as FeatureKey[];

/**
 * Lookup helper. Returns null for unknown keys (defensive: render paths
 * may run before TS catches a typo if the data is reloaded at runtime).
 */
export function getFeature(key: string): FeatureMeta | null {
  return key in FEATURES ? FEATURES[key as FeatureKey] : null;
}

/**
 * Forward lookup — which features does a given project have?
 * Iterates FEATURE_KEYS in declaration order, so chips render in a
 * consistent order across every project (alphabetical here).
 */
export function getProjectFeatures(slug: string): FeatureKey[] {
  return FEATURE_KEYS.filter((key) =>
    (FEATURES[key].projects as readonly string[]).includes(slug),
  );
}

/**
 * Reverse lookup — which projects have a given feature?
 * Returns the canonical slug list straight from the registry.
 */
export function getProjectsForFeature(key: FeatureKey): readonly ProjectSlug[] {
  return FEATURES[key].projects;
}
